import React, { type MouseEvent, useEffect, useMemo, useState } from 'react'

import { Search } from 'lucide-react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Schedule } from '@/core/api/entities/schedule'
import { Song } from '@/core/api/entities/song/song'
import {
	MAX_PLAYLISTS_PER_GROUP_OR_ZONE,
	MAX_SCHEDULES_PER_GROUP_OR_ZONE,
} from '@/core/constants/assignmentLimits'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePlaylists, usePlaylistSample } from '@/core/hooks/usePlaylist'
import { useSchedules } from '@/core/hooks/useSchedule'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	TabsBadge,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/atoms/Tabs'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { ModalPaginator } from '@/shared/components/molecules/ModalPaginator/ModalPaginator'
import { PlaylistRow } from '@/shared/components/molecules/PlaylistRow/PlaylistRow'
import { ScheduleRow } from '@/shared/components/molecules/ScheduleRow/ScheduleRow'
import Player from '@/shared/components/organisms/Player/Player'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

type ContentTab = 'playlists' | 'schedules'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 20
const EMPTY_IDS: number[] = []
const EMPTY_CONFLICTS_MAP = new Map<number, string>()

interface AddContentToZoneModalProps {
	visible: boolean
	onClose: () => void
	initialTab?: ContentTab
	initialPlaylistIds?: number[]
	initialScheduleIds?: number[]
	onConfirm: (
		playlistIds: number[],
		scheduleIds: number[]
	) => Promise<void> | void
	isSubmitting?: boolean
	groupPlaylistConflicts?: Map<number, string>
	groupScheduleConflicts?: Map<number, string>
}

const AddContentToZoneModal: React.FC<AddContentToZoneModalProps> = ({
	visible,
	onClose,
	initialTab = 'playlists',
	initialPlaylistIds = EMPTY_IDS,
	initialScheduleIds = EMPTY_IDS,
	onConfirm,
	isSubmitting = false,
	groupPlaylistConflicts = EMPTY_CONFLICTS_MAP,
	groupScheduleConflicts = EMPTY_CONFLICTS_MAP,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const { showNotification } = useNotificationContext()

	const [activeTab, setActiveTab] = useState<ContentTab>(initialTab)
	const [searchQuery, setSearchQuery] = useState('')
	const [playlistsPage, setPlaylistsPage] = useState(DEFAULT_PAGE)
	const [schedulesPage, setSchedulesPage] = useState(DEFAULT_PAGE)
	const [selectedPlaylistIds, setSelectedPlaylistIds] = useState<number[]>([])
	const [selectedScheduleIds, setSelectedScheduleIds] = useState<number[]>([])

	// Audio preview state
	const [playingScheduleId, setPlayingScheduleId] = useState<number | null>(
		null
	)
	const [playingPlaylist, setPlayingPlaylist] = useState<Playlist | null>(null)
	const [currentSong, setCurrentSong] = useState<Song | null>(null)
	const [playlistIdForSample, setPlaylistIdForSample] = useState<number | null>(
		null
	)

	// Debounce search query for server-side search
	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	// Reset page to 1 when search query changes
	useEffect(() => {
		setPlaylistsPage(DEFAULT_PAGE)
		setSchedulesPage(DEFAULT_PAGE)
	}, [debouncedSearchQuery])

	useEffect(() => {
		if (!visible) return
		setActiveTab(initialTab)
		setSearchQuery('')
		setPlaylistsPage(DEFAULT_PAGE)
		setSchedulesPage(DEFAULT_PAGE)
		setSelectedPlaylistIds(initialPlaylistIds)
		setSelectedScheduleIds(initialScheduleIds)
		// Reset audio preview state
		handlePlayerStop()
	}, [visible, initialTab, initialPlaylistIds, initialScheduleIds])

	const { data: playlistsResponse, isLoading: isLoadingPlaylists } =
		usePlaylists({
			config: useAccountConfig({
				params: {
					page: playlistsPage,
					per_page: DEFAULT_PAGE_SIZE,
					query: debouncedSearchQuery || undefined,
				},
			}),
			options: {
				enabled: visible && activeTab === 'playlists',
			},
		})

	const { data: schedulesResponse, isLoading: isLoadingSchedules } =
		useSchedules({
			config: useAccountConfig({
				params: {
					page: schedulesPage,
					per_page: DEFAULT_PAGE_SIZE,
					query: debouncedSearchQuery || undefined,
					include: 'timeslots.timeslot_contents.playlists',
				},
			}),
			options: {
				enabled: visible && activeTab === 'schedules',
			},
		})

	const playlists = playlistsResponse?.data ?? []
	const schedules = schedulesResponse?.data ?? []
	const playlistsTotalPages =
		playlistsResponse?.meta?.pagination?.total_pages ?? 1
	const schedulesTotalPages =
		schedulesResponse?.meta?.pagination?.total_pages ?? 1

	// Fetch sample for the selected playlist
	const { data: sampleSongs, isLoading: isLoadingSample } = usePlaylistSample({
		playlistId: playlistIdForSample,
		config: useAccountConfig({}),
		options: {
			enabled: visible && playlistIdForSample !== null,
		},
	})

	// Set current song when sample data is loaded
	useEffect(() => {
		if (sampleSongs && sampleSongs.length > 0) {
			setCurrentSong(sampleSongs[0])
		}
	}, [sampleSongs])

	const selectedPlaylistsCount = useMemo(
		() => selectedPlaylistIds.length,
		[selectedPlaylistIds]
	)
	const selectedSchedulesCount = useMemo(
		() => selectedScheduleIds.length,
		[selectedScheduleIds]
	)

	const handleTogglePlaylist = (playlist: Playlist) => {
		if (groupPlaylistConflicts.has(playlist.id)) return

		const isSelected = selectedPlaylistIds.includes(playlist.id)
		if (
			!isSelected &&
			selectedPlaylistIds.length >= MAX_PLAYLISTS_PER_GROUP_OR_ZONE
		) {
			showNotification({
				type: 'error',
				message: t('details.addContentModal.limits.maxPlaylistsExceeded', {
					max: MAX_PLAYLISTS_PER_GROUP_OR_ZONE,
				}),
			})
			return
		}

		setSelectedPlaylistIds((previous) =>
			previous.includes(playlist.id)
				? previous.filter((id) => id !== playlist.id)
				: [...previous, playlist.id]
		)
	}

	const handleToggleSchedule = (schedule: Schedule) => {
		if (groupScheduleConflicts.has(schedule.id)) return

		const isSelected = selectedScheduleIds.includes(schedule.id)
		if (
			!isSelected &&
			selectedScheduleIds.length >= MAX_SCHEDULES_PER_GROUP_OR_ZONE
		) {
			showNotification({
				type: 'error',
				message: t('details.addContentModal.limits.maxSchedulesExceeded', {
					max: MAX_SCHEDULES_PER_GROUP_OR_ZONE,
				}),
			})
			return
		}

		setSelectedScheduleIds((previous) =>
			previous.includes(schedule.id)
				? previous.filter((id) => id !== schedule.id)
				: [...previous, schedule.id]
		)
	}

	const handlePlaylistRowPlayClick = (
		playlist: Playlist,
		e: MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation()
		if (playingScheduleId === null && playlistIdForSample === playlist.id) {
			handlePlayerStop()
		} else {
			setPlayingScheduleId(null)
			setPlaylistIdForSample(playlist.id)
			setPlayingPlaylist(playlist)
			setCurrentSong(null)
		}
	}

	const handlePlaylistPlayClick = (
		scheduleId: number,
		playlist: Playlist,
		e: MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation()
		if (
			playingScheduleId === scheduleId &&
			playlistIdForSample === playlist.id
		) {
			// Stop playing
			handlePlayerStop()
		} else {
			// Start playing this playlist sample
			setPlayingScheduleId(scheduleId)
			setPlaylistIdForSample(playlist.id)
			setPlayingPlaylist(playlist)
			setCurrentSong(null)
		}
	}

	const getSchedulePlaylists = (schedule: Schedule) => {
		const playlistsMap = new Map<number, Playlist>()
		schedule.timeslots?.forEach((timeslot) => {
			timeslot.timeslotContents?.forEach((content) => {
				content.playlists?.forEach((playlist) => {
					playlistsMap.set(playlist.id, playlist)
				})
			})
		})
		return Array.from(playlistsMap.values())
	}

	// Get the currently playing schedule data
	const playingSchedule = schedules.find((s) => s.id === playingScheduleId)

	// Handle player pause/stop
	const handlePlayerStop = () => {
		setPlayingScheduleId(null)
		setPlayingPlaylist(null)
		setPlaylistIdForSample(null)
		setCurrentSong(null)
	}

	const handleCancel = () => {
		if (isSubmitting) return
		onClose()
	}

	const handleConfirm = async () => {
		await onConfirm(selectedPlaylistIds, selectedScheduleIds)
	}

	return (
		<Dialog open={visible} onOpenChange={(open) => !open && handleCancel()}>
			<DialogContent
				className="max-w-full sm:max-w-3xl p-0 gap-0 flex flex-col max-h-[100dvh] sm:max-h-[90vh] overflow-hidden rounded-none sm:rounded-lg"
				showCloseButton={!isSubmitting}
			>
				<DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2 sm:pb-3">
					<DialogTitle className="text-xl">
						{t('details.addContentModal.title')}
					</DialogTitle>
				</DialogHeader>
				<Separator />

				<div className="px-4 sm:px-6 py-2 sm:py-3 flex flex-col flex-1 min-h-0 overflow-hidden">
					<Tabs
						value={activeTab}
						onValueChange={(value) => setActiveTab(value as ContentTab)}
						className="flex-1 flex flex-col min-h-0"
					>
						<TabsList className="flex-none mb-3">
							<TabsTrigger value="playlists">
								{t('details.addContentModal.tabs.playlists')}
								<TabsBadge>{selectedPlaylistsCount}</TabsBadge>
							</TabsTrigger>
							<TabsTrigger value="schedules">
								{t('details.addContentModal.tabs.schedules')}
								<TabsBadge>{selectedSchedulesCount}</TabsBadge>
							</TabsTrigger>
						</TabsList>

						<div className="relative flex-none mb-3">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								placeholder={t('details.addContentModal.searchPlaceholder')}
								value={searchQuery}
								onChange={(event) => setSearchQuery(event.target.value)}
								className="pl-10"
							/>
						</div>

						<TabsContent
							value="playlists"
							className="mt-0 flex-1 flex flex-col min-h-0"
						>
							<ScrollArea className="h-[calc(100dvh-340px)] sm:h-[calc(90vh-340px)] border rounded-md">
								<div className="h-full p-2">
									{isLoadingPlaylists ? (
										<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
											{t('details.addContentModal.loadingPlaylists')}
										</div>
									) : playlists.length === 0 ? (
										<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
											{t('details.addContentModal.noPlaylists')}
										</div>
									) : (
										<div className="pb-10">
											{playlists.map((playlist: Playlist) => {
												const conflictGroup = groupPlaylistConflicts.get(
													playlist.id
												)
												return (
													<PlaylistRow
														key={`playlist-${playlist.id}`}
														playlist={playlist}
														isSelected={selectedPlaylistIds.includes(
															playlist.id
														)}
														onSelectionChange={() =>
															handleTogglePlaylist(playlist)
														}
														isPlaying={playlistIdForSample === playlist.id}
														isLoadingPlayback={
															isLoadingSample &&
															playlistIdForSample === playlist.id
														}
														isPlaybackReady={Boolean(currentSong)}
														onPlayClick={(event) =>
															handlePlaylistRowPlayClick(playlist, event)
														}
														disabled={!!conflictGroup}
														disabledReason={
															conflictGroup
																? t(
																		'details.addContentModal.limits.alreadyAssignedByGroup',
																		{ groupName: conflictGroup }
																	)
																: undefined
														}
													/>
												)
											})}
										</div>
									)}
								</div>
							</ScrollArea>
							<ModalPaginator
								currentPage={playlistsPage}
								totalPages={playlistsTotalPages}
								onPageChange={setPlaylistsPage}
							/>
						</TabsContent>

						<TabsContent
							value="schedules"
							className="mt-0 flex-1 flex flex-col min-h-0"
						>
							<ScrollArea className="h-[calc(100dvh-340px)] sm:h-[calc(90vh-340px)] border rounded-md">
								<div className="h-full p-2">
									{isLoadingSchedules ? (
										<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
											{t('details.addContentModal.loadingSchedules')}
										</div>
									) : schedules.length === 0 ? (
										<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
											{t('details.addContentModal.noSchedules')}
										</div>
									) : (
										<div className="divide-y pb-10">
											{schedules.map((schedule: Schedule) => {
												const conflictGroup = groupScheduleConflicts.get(
													schedule.id
												)
												return (
													<ScheduleRow
														key={`schedule-${schedule.id}`}
														schedule={schedule}
														isSelected={selectedScheduleIds.includes(
															schedule.id
														)}
														onSelectionChange={() =>
															handleToggleSchedule(schedule)
														}
														playlists={getSchedulePlaylists(schedule)}
														playingScheduleId={playingScheduleId}
														playlistIdForSample={playlistIdForSample}
														isLoadingSample={isLoadingSample}
														currentSong={currentSong}
														onPlaylistPlayClick={handlePlaylistPlayClick}
														disabled={!!conflictGroup}
														disabledReason={
															conflictGroup
																? t(
																		'details.addContentModal.limits.alreadyAssignedByGroup',
																		{ groupName: conflictGroup }
																	)
																: undefined
														}
													/>
												)
											})}
										</div>
									)}
								</div>
							</ScrollArea>
							<ModalPaginator
								currentPage={schedulesPage}
								totalPages={schedulesTotalPages}
								onPageChange={setSchedulesPage}
							/>
						</TabsContent>
					</Tabs>
				</div>

				<Separator />
				<DialogFooter className="px-4 sm:px-6 py-3 sm:py-4">
					<Button
						type="button"
						variant="outline"
						onClick={handleCancel}
						className="h-9 rounded-full px-6"
						disabled={isSubmitting}
					>
						{tCommon('actions.cancel')}
					</Button>
					<Button
						type="button"
						onClick={handleConfirm}
						className="h-9 rounded-full px-6"
						disabled={isSubmitting}
					>
						{isSubmitting
							? t('details.addContentModal.saving')
							: t('details.addContentModal.confirm')}
					</Button>
				</DialogFooter>
			</DialogContent>

			{/* Hidden audio player for schedule and playlist cover previews */}
			{currentSong && playingPlaylist && (
				<div className="hidden">
					<Player
						src={currentSong.path}
						cover={playingPlaylist.getSmallestThumbnail()}
						title={
							currentSong.metadata.title ?? t('details.addContentModal.loading')
						}
						radioPrevText={t('details.addContentModal.preview')}
						radioTitle={playingSchedule?.title || playingPlaylist.title}
						metadata={[
							currentSong.metadata.artist || '',
							currentSong.metadata.album || '',
						].filter(Boolean)}
						showSkipControls={false}
						showJumpControls={false}
						autoPlay={true}
						onPause={handlePlayerStop}
						hideVolume={true}
						cssVars={{
							border: 'none',
							backgroundColor: 'transparent',
						}}
					/>
				</div>
			)}
		</Dialog>
	)
}

export default AddContentToZoneModal
